import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { SizeChips } from "@/components/SizeChips";
import { toast } from "sonner";
import { Tags, Plus, Pencil, Loader2, X, Factory, Home } from "lucide-react";

const emptyForm = {
  nome: "", descrizione: "", produzione_default: "interna", richiede_taglia: true,
  taglie_default: [], consente_nome_retro: false, includi_export_fabbrica: false,
  attivo: true, ordine_visualizzazione: 0,
};

export default function CatalogoArticoli() {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | {form, id?}
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/raccolta/catalogo").then(({ data }) => setLista(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => setModal({ form: { ...emptyForm, ordine_visualizzazione: lista.length } });
  const openEdit = (a) => setModal({ id: a.id, form: { ...a } });
  const set = (k, v) => setModal((m) => ({ ...m, form: { ...m.form, [k]: v } }));

  const save = async () => {
    if (!modal.form.nome.trim()) return toast.error("Il nome è obbligatorio");
    setSaving(true);
    try {
      if (modal.id) await api.patch(`/raccolta/catalogo/${modal.id}`, modal.form);
      else await api.post("/raccolta/catalogo", modal.form);
      toast.success("Articolo salvato");
      setModal(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (a) => {
    try { await api.patch(`/raccolta/catalogo/${a.id}`, { attivo: !a.attivo }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Layout>
      <div className="mb-6 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-violet-400/15 border border-violet-400/30 flex items-center justify-center">
            <Tags className="w-5 h-5 text-violet-300" />
          </div>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-100 tracking-tight">Catalogo Articoli</h1>
            <p className="text-slate-500 text-sm">Articoli configurabili usati nelle raccolte ordini</p>
          </div>
        </div>
        <button data-testid="catalogo-new-btn" onClick={openNew} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold text-sm">
          <Plus className="w-4 h-4" /> Nuovo Articolo
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /> Caricamento...</div>
      ) : (
        <div className="glass rounded-xl overflow-hidden" data-testid="catalogo-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/60 text-slate-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Nome</th>
                <th className="text-left px-4 py-3">Produzione</th>
                <th className="text-center px-4 py-3">Taglia</th>
                <th className="text-center px-4 py-3">Nome retro</th>
                <th className="text-center px-4 py-3">Export fabbrica</th>
                <th className="text-center px-4 py-3">Stato</th>
                <th className="text-right px-4 py-3">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((a) => (
                <tr key={a.id} data-testid={`catalogo-row-${a.id}`} className="border-t border-slate-800/60">
                  <td className="px-4 py-3 text-slate-200 font-medium">{a.nome}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${a.produzione_default === "esterna" ? "bg-orange-400/15 text-orange-300 border-orange-400/30" : "bg-sky-400/15 text-sky-300 border-sky-400/30"}`}>
                      {a.produzione_default === "esterna" ? <Factory className="w-3 h-3" /> : <Home className="w-3 h-3" />}
                      {a.produzione_default === "esterna" ? "Esterna" : "Interna"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center text-slate-400">{a.richiede_taglia ? "Sì" : "No"}</td>
                  <td className="px-4 py-3 text-center text-slate-400">{a.consente_nome_retro ? "Sì" : "No"}</td>
                  <td className="px-4 py-3 text-center text-slate-400">{a.includi_export_fabbrica ? "Sì" : "No"}</td>
                  <td className="px-4 py-3 text-center">
                    <button data-testid={`catalogo-toggle-${a.id}`} onClick={() => toggle(a)} className={`text-xs px-2 py-0.5 rounded-full border ${a.attivo ? "bg-emerald-400/15 text-emerald-300 border-emerald-400/30" : "bg-slate-600/20 text-slate-400 border-slate-600/40"}`}>
                      {a.attivo ? "Attivo" : "Disattivo"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`catalogo-edit-${a.id}`} onClick={() => openEdit(a)} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-slate-700/40 text-slate-300 border border-slate-700 hover:bg-slate-700"><Pencil className="w-3.5 h-3.5" /> Modifica</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" onClick={() => setModal(null)}>
          <div className="glass rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="catalogo-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-100">{modal.id ? "Modifica articolo" : "Nuovo articolo"}</h2>
              <button onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-200"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Nome</label>
                <input data-testid="catalogo-nome-input" value={modal.form.nome} onChange={(e) => set("nome", e.target.value)} className="mt-1.5 w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 focus:outline-none focus:border-cyan-400/60" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Descrizione (opzionale)</label>
                <input value={modal.form.descrizione || ""} onChange={(e) => set("descrizione", e.target.value)} className="mt-1.5 w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 focus:outline-none focus:border-cyan-400/60" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Produzione</label>
                <div className="flex gap-2 mt-1.5">
                  {["interna", "esterna"].map((p) => (
                    <button key={p} type="button" data-testid={`catalogo-prod-${p}`} onClick={() => set("produzione_default", p)} className={`flex-1 py-2 rounded-lg text-sm border ${modal.form.produzione_default === p ? "bg-cyan-400/15 text-cyan-300 border-cyan-400/40" : "text-slate-400 border-slate-700"}`}>{p === "interna" ? "Interna" : "Esterna (Fabbrica)"}</button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 text-sm text-slate-300"><input data-testid="catalogo-richiede-taglia" type="checkbox" checked={modal.form.richiede_taglia} onChange={(e) => set("richiede_taglia", e.target.checked)} /> Richiede taglia</label>
                <label className="flex items-center gap-2 text-sm text-slate-300"><input data-testid="catalogo-nome-retro" type="checkbox" checked={modal.form.consente_nome_retro} onChange={(e) => set("consente_nome_retro", e.target.checked)} /> Consente Nome retro</label>
                <label className="flex items-center gap-2 text-sm text-slate-300"><input data-testid="catalogo-export-fabbrica" type="checkbox" checked={modal.form.includi_export_fabbrica} onChange={(e) => set("includi_export_fabbrica", e.target.checked)} /> Includi nell'export Fabbrica</label>
                <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={modal.form.attivo} onChange={(e) => set("attivo", e.target.checked)} /> Attivo</label>
              </div>
              {modal.form.richiede_taglia && (
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Taglie di default</label>
                  <div className="mt-1.5"><SizeChips testid="catalogo-taglie" value={modal.form.taglie_default} onChange={(v) => set("taglie_default", v)} /></div>
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={() => setModal(null)} className="flex-1 py-2.5 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800">Annulla</button>
              <button data-testid="catalogo-save-btn" onClick={save} disabled={saving} className="flex-1 py-2.5 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Salva
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
